import { useCallback, useRef, useState } from 'react';
import { UserRoles } from '@/enums/enums';
import { useDeleteRequestCategoryMutation } from '@/features/requests/requests.mutations';
import { useRequestCategoriesAll } from '@/features/requests/requests.queries';
import { getUserOptions } from '@/features/user/user.queries';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { TableDateColumn } from '@/components/datatable/data-table-simple/data-table-custom-columns';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import AlertModal from '@/components/modals/alert-modal';
import RequestCategoryCreateUpdateModal from '@/components/modals/request-category-create-update-modal';

const RequestSettingsRouteSchema = z.object({
  mode: z.enum(['create', 'update']).optional(),
  category: z.number().optional(),
});

export const Route = createFileRoute('/_app/(requests)/requests/settings')({
  validateSearch: zodSearchValidator(RequestSettingsRouteSchema),
  component: RequestSettingsPage,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (![UserRoles.Root, UserRoles.Admin].includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function RequestSettingsPage() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <RequestCategoryHeader />
      <RequestCategoryTable />
    </div>
  );
}

function RequestCategoryHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['requests']);

  const onCreateNewCategory = () => {
    navigate({
      to: '.',
      search: { mode: 'create' },
    });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('settings.page_title')}</span>
        <span className='text-base font-light text-neutral-500'>
          {t('settings.page_sub_title')}
        </span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={onCreateNewCategory} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {t('settings.new_category')}
        </Button>
      </div>
      <RequestCategoryCreateUpdateModal />
    </div>
  );
}

function RequestCategoryTable() {
  const { t } = useTranslation(['table']);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const selectedDeleteId = useRef<number>(0);

  const navigate = useNavigate();
  const { data = [], isLoading } = useRequestCategoriesAll();

  const deleteMutation = useDeleteRequestCategoryMutation();

  const onEditCategory = (id: number) => {
    navigate({
      to: '.',
      search: { mode: 'update', category: id },
    });
  };

  const deleteCategory = (id: number) => {
    selectedDeleteId.current = id;
    setDeleteOpen(true);
  };

  const onSuccessDelete = useCallback(async () => {
    await deleteMutation.mutateAsync(selectedDeleteId.current);
  }, [deleteMutation]);

  return (
    <>
      <DataTableSimple
        data={data}
        columns={['name', 'createdAt', 'updatedAt']}
        columnsLabels={{
          name: t('name'),
          createdAt: t('created_at'),
          updatedAt: t('updated_at'),
        }}
        extraColumns={[
          {
            id: 'actions',
            size: 50,
            cell: ({ row }) => (
              <DataTableRowActions
                row={row}
                menuItems={[
                  {
                    label: 'Επεξεργασία',
                    onClick: () => onEditCategory(row.original.id),
                  },
                  {
                    label: 'Διαγραφή',
                    onClick: () => deleteCategory(row.original.id),
                  },
                ]}
              />
            ),
          },
        ]}
        customRender={{
          createdAt: (row) => <TableDateColumn date={row.getValue('createdAt')} isISO />,
          updatedAt: (row) => <TableDateColumn date={row.getValue('updatedAt')} isISO />,
        }}
        isLoading={isLoading}
        className='flex h-full flex-col overflow-y-auto py-4'
      />
      <AlertModal
        title='Είστε σίγουροι;'
        description='Αυτή η ενέργεια δεν μπορεί να αναιρεθεί. Η κατηγορία αιτήματος θα διαγραφεί οριστικά.'
        setOpen={setDeleteOpen}
        isOpen={isDeleteOpen}
        onSuccess={onSuccessDelete}
      />
    </>
  );
}
